import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Globe,
  LayoutGrid,
  Mail,
  MessageCircle,
  Sparkles,
  Star,
  Users,
  Video,
  Zap,
} from 'lucide-react';
import AppHeader from './AppHeader';
import AuthEntryButtons from './AuthEntryButtons';
import TestimonialScreensCarousel from './TestimonialScreensCarousel';
import TherapistPlansLayout from './Plans/TherapistPlansLayout';
import InfoModal from './InfoModal';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { PlanService } from '../services/PlanService';
import { PlanAccessValidator } from '../services/PlanAccessValidator';
import { UserType } from '../types/plan';
import { PlanDisplay } from './Plans/PlanCard';
import { captureInfluencerRefFromSearch } from '../utils/influencerAttribution';
import { isMarketingHost, shouldEnforceDomainSplit } from '../constants/siteDomains';
import { goToApp } from '../utils/crossDomainNav';
import { CHECKOUT_MESSAGES } from '../constants/checkout';
import {
  CONSTELADOR_SEO_PHRASE,
  CONSTELADOR_VIDEO_ID,
  CONSTELADOR_SHORT_VIDEO_URL,
  CONSTELADOR_WHATSAPP,
  consteladorAudience,
  consteladorBenefits,
  consteladorFaqGeneral,
  consteladorFaqPurchase,
  consteladorFeatures,
  consteladorPurchaseSteps,
  consteladorHeroTestimonialScreens,
  consteladorTestimonials,
  consteladorValuePillars,
} from '../constants/consteladorLanding';

/** Ex.: "R$ 69,90 (R$ 17,47/h)" ou "R$ 19,90" */
const PRICE_IN_TEXT_RE = /R\$ [\d.,]+(?:\s*\(R\$ [\d.,]+\/h\))?/g;

const keepCurrencyTogether = (price: string): string =>
  price.replace(/R\$\s+/g, 'R$\u00A0');

/** Evita quebra de linha entre R$ e o valor. */
function renderTextWithInlinePrices(text: string): React.ReactNode {
  const segments: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(PRICE_IN_TEXT_RE.source, 'g');
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(text.slice(lastIndex, match.index));
    }
    segments.push(
      <span key={match.index} className="whitespace-nowrap">
        {keepCurrencyTogether(match[0])}
      </span>
    );
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }
  return segments.length === 1 ? segments[0] : segments;
}

const featureIcons = [Users, Video, CheckCircle2, Globe];
const pillarIcons = [Sparkles, Star, LayoutGrid];

const ConsteladorPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState<PlanDisplay[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'warning' as 'error' | 'warning' | 'info' | 'success',
    redirectTo: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${CONSTELADOR_SEO_PHRASE} | Planos para Consteladores | Constela Brasil`;
    return () => {
      document.title = 'Constela Brasil';
    };
  }, []);

  useEffect(() => {
    captureInfluencerRefFromSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const allPlans = await PlanService.fetchActivePlans(db);
        const therapistPlans = allPlans
          .filter((plan) => plan.userType === UserType.THERAPIST)
          .map((basePlan) => ({
            ...basePlan,
            type: 'terapeuta' as const,
            title: basePlan.name,
            price: PlanService.formatCurrency(basePlan.value),
            period: PlanService.formatPeriod(basePlan.quantity, basePlan.billingType, basePlan.isRecurring),
            requiresScheduling: false,
            actualPrice: basePlan.value,
          }))
          .sort((a, b) => (a.value || 0) - (b.value || 0));
        setPlans(therapistPlans);
      } catch (error) {
        console.error('Erro ao buscar planos:', error);
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  const buildCheckoutPath = (planId: string, quantity?: number) => {
    const params = new URLSearchParams({ planId });
    if (quantity && Number.isInteger(quantity) && quantity > 0) {
      params.set('quantity', String(quantity));
    }
    return `/checkout?${params.toString()}`;
  };

  const handlePlanSelection = async (planId: string, quantity?: number) => {
    const checkoutPath = buildCheckoutPath(planId, quantity);

    if (shouldEnforceDomainSplit() && isMarketingHost()) {
      goToApp(checkoutPath);
      return;
    }

    if (!isAuthenticated) {
      navigate(checkoutPath, {
        state: { from: { pathname: location.pathname, search: location.search } },
      });
      return;
    }

    const selectedPlan = plans.find((plan) => plan.id === planId);
    if (!selectedPlan) return;

    if (user?.role && selectedPlan.userType) {
      const validation = PlanAccessValidator.canAccessPlan(user.role, selectedPlan.userType);
      if (!validation.canAccess) {
        setModalState({
          isOpen: true,
          title: validation.title || 'Acesso negado',
          message: validation.message || '',
          variant: validation.variant || 'warning',
          redirectTo: validation.redirectTo || '',
        });
        return;
      }
    }

    setProcessingPlan(planId);
    try {
      if (selectedPlan.redirectTo) {
        navigate(selectedPlan.redirectTo);
        return;
      }
      if (selectedPlan.requiresScheduling) {
        goToApp(`/agendamento?plan=${planId}`, navigate);
        return;
      }
      navigate(checkoutPath, {
        state: { from: { pathname: location.pathname, search: location.search } },
      });
    } catch (error) {
      console.error('Erro ao processar seleção do plano:', error);
      setModalState({
        isOpen: true,
        title: 'Erro',
        message: 'Erro ao processar sua solicitação. Por favor, tente novamente.',
        variant: 'error',
        redirectTo: '',
      });
    } finally {
      setProcessingPlan(null);
    }
  };

  const { founder } = CHECKOUT_MESSAGES;

  return (
    <div className="min-h-screen bg-[#F3F6F1] text-[#3A5859]">
      <AppHeader />

      <main>
        {/* Hero — entendimento imediato */}
        <section className="relative overflow-hidden pt-28 pb-12 sm:pt-32 sm:pb-16">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#3A5859]/10 via-transparent to-[#A56096]/10" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#A56096]">
                {CONSTELADOR_SEO_PHRASE}
              </p>
              <h1 className="mt-4 text-3xl font-extrabold leading-tight text-[#3A5859] sm:text-4xl lg:text-5xl">
                A plataforma feita para você conduzir Constelação Familiar online com profissionalismo
              </h1>
              <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-[#496566] sm:text-xl">
                Se você é constelador e quer sair do improviso, esta é a{' '}
                <strong className="font-semibold text-[#3A5859]">{CONSTELADOR_SEO_PHRASE}</strong> que reúne
                cenário virtual, avatares interativos e videochamada, tudo integrado em um só lugar.
              </p>

              <div className="mx-auto mt-10 max-w-3xl overflow-hidden">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#A56096]">
                  Depoimentos reais
                </p>
                <TestimonialScreensCarousel items={consteladorHeroTestimonialScreens} />
              </div>

              <div className="mt-8 space-y-4">
                <AuthEntryButtons variant="hero" showTrialHint className="mx-auto max-w-xl" />
                <a
                  href="#planos"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#A56096]/30 bg-white px-6 py-3 text-sm font-semibold text-[#A56096] transition hover:bg-[#A56096]/5"
                >
                  Ver planos
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="relative mx-auto mt-12 max-w-4xl">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#A56096]/20 to-[#3A5859]/10 blur-2xl" aria-hidden />
              <img
                src="/assets/checkout/plataforma-preview.png"
                alt="Plataforma para Constelação Familiar com cenário virtual, avatares e videochamada"
                className="relative w-full rounded-[1.5rem] border border-[#E8EFEB] shadow-2xl"
              />
            </div>
          </div>
        </section>

        {/* 3 pilares — o que muda na prática */}
        <section className="border-y border-[#E8EFEB] bg-white py-14 sm:py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-2xl font-bold text-[#3A5859] sm:text-3xl">
              O que muda quando você usa a plataforma
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {consteladorValuePillars.map((pillar, index) => {
                const Icon = pillarIcons[index] ?? Sparkles;
                return (
                  <article
                    key={pillar.title}
                    className="rounded-[1.5rem] border border-[#E8EFEB] bg-[#F3F6F1] p-6 text-center md:text-left"
                  >
                    <div className="mx-auto mb-4 inline-flex rounded-2xl bg-[#A56096]/10 p-3 md:mx-0">
                      <Icon className="h-6 w-6 text-[#A56096]" aria-hidden />
                    </div>
                    <h3 className="text-lg font-bold text-[#3A5859]">{pillar.title}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#496566]">{pillar.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Vídeo */}
        <section id="video" className="py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#A56096]">
              Veja como funciona
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#3A5859] sm:text-4xl">
              Tecnologia a serviço da Alma
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-[#496566]">
              Assista ao vídeo e entenda, em poucos minutos, como a {CONSTELADOR_SEO_PHRASE} transforma
              seu atendimento online.
            </p>
            <div className="mkt-video mx-auto mt-8 max-w-3xl">
              <iframe
                src={`https://www.youtube.com/embed/${CONSTELADOR_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1`}
                title={`${CONSTELADOR_SEO_PHRASE} | Constela Brasil`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <a
              href={CONSTELADOR_SHORT_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mx-auto mt-6 flex max-w-3xl items-center gap-4 rounded-2xl border border-[#E8EFEB] bg-white p-5 text-left shadow-sm transition hover:border-[#A56096]/40 hover:shadow-md"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#A56096]/10">
                <Video className="h-6 w-6 text-[#A56096]" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-[#3A5859]">Resumo em 1 minuto</p>
                <p className="mt-1 text-sm text-[#496566]">Assista ao vídeo curto no YouTube</p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-[#A56096]" aria-hidden />
            </a>
          </div>
        </section>

        {/* Recursos */}
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-[#3A5859] sm:text-4xl">O que você encontra na plataforma?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-[#496566]">
                Não é só uma videochamada. É uma estrutura profissional pensada para Constelação Familiar
                online, individual ou em grupo.
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              {consteladorFeatures.map((feature, index) => {
                const Icon = featureIcons[index] ?? Sparkles;
                return (
                  <article
                    key={feature.title}
                    className="rounded-[1.5rem] border border-[#E8EFEB] bg-[#F3F6F1] p-6 shadow-sm"
                  >
                    <div className="mb-4 inline-flex rounded-2xl bg-[#A56096]/10 p-3">
                      <Icon className="h-6 w-6 text-[#A56096]" aria-hidden />
                    </div>
                    <h3 className="text-xl font-bold text-[#3A5859]">{feature.title}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#496566]">{feature.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Passo a passo + tutorial + acesso imediato */}
        <section id="como-contratar" className="scroll-mt-24 py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-[#3A5859] sm:text-4xl">Como contratar: passo a passo</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-[#496566]">
                Simples, rápido e com suporte. Em poucos minutos você já pode estar usando a plataforma.
              </p>
            </div>

            <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {consteladorPurchaseSteps.map((item) => (
                <li
                  key={item.step}
                  className="relative rounded-2xl border border-[#E8EFEB] bg-white p-5 shadow-sm"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#A56096] text-sm font-bold text-white">
                    {item.step}
                  </span>
                  <h3 className="mt-3 font-bold text-[#3A5859]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#496566]">{item.text}</p>
                </li>
              ))}
            </ol>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <article className="flex gap-4 rounded-2xl border-2 border-[#A56096]/40 bg-[#A56096]/5 p-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#A56096] text-white">
                  <Zap className="h-6 w-6" aria-hidden />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#3A5859]">Acesso imediato</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#496566]">
                    Pagamento confirmado? Você já entra na plataforma e acessa sua sala virtual na hora,
                    sem burocracia e sem espera.
                  </p>
                </div>
              </article>
              <article className="flex gap-4 rounded-2xl border border-[#E8EFEB] bg-white p-6 shadow-sm">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#3A5859] text-white">
                  <Mail className="h-6 w-6" aria-hidden />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#3A5859]">Tutorial por e-mail</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#496566]">
                    Logo após a contratação, enviamos um guia completo por e-mail com o passo a passo para
                    usar a sala, os avatares e a videochamada. Dúvidas? Suporte pelo WhatsApp.
                  </p>
                </div>
              </article>
            </div>

            <div className="mx-auto mt-10 max-w-2xl rounded-2xl border-2 border-[#A56096]/30 bg-white p-6 text-center shadow-sm">
              <p className="text-lg font-bold text-[#A56096] sm:text-xl">
                Experimente gratuitamente: cadastre o seu e-mail e use agora
              </p>
              <AuthEntryButtons variant="hero" className="mt-5" />
            </div>

            <div className="mt-8 text-center">
              <a
                href="#planos"
                className="inline-flex items-center gap-2 rounded-full bg-[#3A5859] px-8 py-4 text-base font-semibold text-white transition hover:bg-[#496566]"
              >
                Escolher meu plano
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </section>

        {/* Depoimentos — antes dos planos para gerar confiança */}
        <section id="depoimentos" className="bg-[#3A5859] py-16 text-white sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold sm:text-4xl">Depoimentos de Consteladores</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">
                Veja o que profissionais que já utilizam a plataforma dizem sobre a experiência.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {consteladorTestimonials.map((item) => (
                <blockquote
                  key={item.quote.slice(0, 40)}
                  className="rounded-[1.5rem] border border-white/10 bg-white/5 p-6 backdrop-blur"
                >
                  <p className="text-[15px] leading-relaxed text-white/90">&ldquo;{item.quote}&rdquo;</p>
                  <footer className="mt-4 text-sm font-semibold text-[#E7B7D6]">{item.author}</footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        {/* Benefícios */}
        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-[#3A5859] sm:text-4xl">O que você conquista ao entrar?</h2>
              <div className="mt-6 inline-flex items-center gap-1 rounded-full bg-[#3A5859] px-4 py-2 text-sm font-semibold text-white">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                ))}
                <span className="ml-1">5 estrelas</span>
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {consteladorBenefits.map((benefit) => (
                <article
                  key={benefit.title}
                  className="rounded-2xl border border-[#E8EFEB] bg-white p-6 shadow-sm"
                >
                  <p className="text-2xl" aria-hidden>
                    {benefit.icon}
                  </p>
                  <h3 className="mt-3 text-lg font-bold text-[#3A5859]">{benefit.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#496566]">{benefit.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Modalidades */}
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-[#3A5859] sm:text-4xl">Duas formas de contratar</h2>
              <p className="mx-auto mt-4 max-w-3xl text-lg text-[#496566]">
                Escolha a modalidade que combina com o seu momento profissional. Em ambas:{' '}
                <strong className="text-[#3A5859]">acesso imediato</strong> e tutorial por e-mail.
              </p>
            </div>
            <div className="grid gap-8 lg:grid-cols-2">
              <article className="rounded-[1.75rem] border border-[#E8EFEB] bg-[#F3F6F1] p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#A56096]">Opção 1 · Sem mensalidade</p>
                <h3 className="mt-2 text-2xl font-bold text-[#3A5859]">Compra avulsa ou pacote de horas</h3>
                <p className="mt-3 text-[#496566]">
                  {renderTextWithInlinePrices(
                    'Ideal para quem deseja utilizar a plataforma conforme a demanda. Hora avulsa a R$ 19,90 ou pacote de 4 horas por R$ 69,90.',
                  )}
                </p>
                <ul className="mt-6 space-y-2 text-sm text-[#496566]">
                  {[
                    'Pague apenas pelas horas que usar',
                    'Sem mensalidade e sem compromisso fixo',
                    'Hora avulsa: R$ 19,90',
                    'Pacote de 4 horas: R$ 69,90 (R$ 17,47/h)',
                    'Acesso imediato após a compra',
                    'Tutorial enviado por e-mail',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#A56096]" />
                      <span className="min-w-0">{renderTextWithInlinePrices(item)}</span>
                    </li>
                  ))}
                </ul>
              </article>
              <article className="rounded-[1.75rem] border-2 border-[#A56096] bg-white p-8 shadow-lg">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#A56096]">Opção 2 · Mais popular</p>
                <h3 className="mt-2 text-2xl font-bold text-[#3A5859]">Planos Associados (homologado)</h3>
                <p className="mt-3 text-[#496566]">
                  Assinatura mensal para quem deseja crescimento contínuo, homologação e visibilidade na vitrine.
                  Três níveis conforme o seu volume de atendimento.
                </p>
                <ul className="mt-6 space-y-2 text-sm text-[#496566]">
                  {[
                    '10 horas/mês: R$ 139,90 (R$ 13,99/h)',
                    '15 horas/mês: R$ 189,90 (R$ 12,66/h)',
                    '25 horas/mês: R$ 225,90 (R$ 9,03/h)',
                    'Profissional homologado e visível na vitrine',
                    'Mais autoridade e novos clientes',
                    'Acesso imediato e tutorial por e-mail',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#A56096]" />
                      <span className="min-w-0">{renderTextWithInlinePrices(item)}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>

        {/* Planos */}
        <section id="planos" className="scroll-mt-24 bg-[#F3F6F1] py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold text-[#3A5859] sm:text-4xl">Escolha seu plano</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-[#496566]">
                Contrate agora e comece a usar a {CONSTELADOR_SEO_PHRASE}.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#A56096]/30 bg-[#A56096]/10 px-5 py-2.5 text-sm font-semibold text-[#8B4A7D]">
                <Zap className="h-4 w-4" aria-hidden />
                Acesso imediato após a confirmação do pagamento
              </div>
              <div className="mx-auto mt-8 max-w-2xl rounded-2xl border-2 border-[#A56096]/30 bg-white p-6 shadow-sm">
                <p className="text-lg font-bold text-[#A56096] sm:text-xl">
                  Experimente gratuitamente: cadastre o seu e-mail e use agora
                </p>
                <AuthEntryButtons variant="hero" className="mt-5" />
              </div>
            </div>
            <TherapistPlansLayout
              plans={plans}
              isLoading={isLoadingPlans}
              processingPlan={processingPlan}
              onSelect={handlePlanSelection}
              showBackLink={false}
            />
            <div className="mx-auto mt-12 max-w-2xl rounded-2xl border-2 border-[#A56096]/30 bg-white p-6 text-center shadow-sm">
              <p className="text-lg font-bold text-[#A56096] sm:text-xl">
                Experimente gratuitamente: cadastre o seu e-mail e use agora
              </p>
              <AuthEntryButtons variant="hero" className="mt-5" />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="duvidas" className="scroll-mt-24 bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-3xl font-bold text-[#3A5859] sm:text-4xl">
              Tire suas dúvidas
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-lg text-[#496566]">
              Perguntas frequentes sobre contratação, acesso, tutorial e uso da plataforma.
            </p>

            <div className="mt-10">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#A56096]">
                Compra e acesso
              </h3>
              <div className="space-y-3">
                {consteladorFaqPurchase.map((item) => (
                  <details
                    key={item.q}
                    className="group rounded-2xl border border-[#E8EFEB] bg-[#F3F6F1] p-5 open:bg-white open:shadow-sm"
                  >
                    <summary className="cursor-pointer list-none font-semibold text-[#3A5859] marker:content-none">
                      {item.q}
                    </summary>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#496566]">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#A56096]">
                Sobre a plataforma
              </h3>
              <div className="space-y-3">
                {consteladorFaqGeneral.map((item) => (
                  <details
                    key={item.q}
                    className="group rounded-2xl border border-[#E8EFEB] bg-[#F3F6F1] p-5 open:bg-white open:shadow-sm"
                  >
                    <summary className="cursor-pointer list-none font-semibold text-[#3A5859] marker:content-none">
                      {item.q}
                    </summary>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#496566]">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="mt-10 text-center">
              <a
                href={CONSTELADOR_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border-2 border-[#3A5859] px-6 py-3 text-sm font-semibold text-[#3A5859] transition hover:bg-[#3A5859] hover:text-white"
              >
                <MessageCircle className="h-4 w-4" />
                Ainda com dúvidas? Fale no WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Para quem é */}
        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold text-[#3A5859] sm:text-4xl">Para quem é?</h2>
              <p className="mx-auto mt-4 max-w-3xl text-lg text-[#496566]">
                Consteladores em diferentes momentos da carreira, de quem está começando a quem já atende e
                quer crescer.
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              {consteladorAudience.map((item) => (
                <article key={item.title} className="rounded-2xl border border-[#E8EFEB] bg-white p-6">
                  <h3 className="text-lg font-bold text-[#3A5859]">{item.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#496566]">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="bg-gradient-to-br from-[#A56096] to-[#8B4A7D] py-16 text-white sm:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Pronto para profissionalizar seu atendimento online?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/90">
              Saia do improviso. Ganhe organização, apresentação profissional e um novo jeito de fazer Constelação,
              com acesso imediato após a contratação.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="#planos"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-[#A56096] shadow-lg transition hover:-translate-y-0.5"
              >
                Contratar agora
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href={CONSTELADOR_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-8 py-4 text-base font-semibold text-white transition hover:bg-white/10"
              >
                <MessageCircle className="h-5 w-5" />
                Falar no WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Idealizadora */}
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-8 rounded-[2rem] border border-[#E8EFEB] bg-[#F3F6F1] p-8 md:grid-cols-[auto_1fr]">
              <img
                src="/assets/checkout/erica-coscioni.png"
                alt={founder.photoAlt}
                className="mx-auto h-40 w-32 rounded-2xl object-cover object-[center_20%] shadow-md md:mx-0"
              />
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#A56096]">A criadora</p>
                <h2 className="mt-2 text-2xl font-bold text-[#3A5859]">{founder.name}</h2>
                <p className="mt-1 font-medium text-[#496566]">Consteladora · Idealizadora da plataforma</p>
                <p className="mt-4 text-[15px] leading-relaxed text-[#496566]">
                  &ldquo;Desenvolvi a Constela Brasil para resolver um problema que eu mesma vivia como consteladora.
                  Criei esta {CONSTELADOR_SEO_PHRASE} para que outros profissionais pudessem atender com a mesma
                  profundidade do presencial, com organização e apresentação profissional.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <InfoModal
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState((prev) => ({ ...prev, isOpen: false }));
          if (modalState.redirectTo) {
            navigate(modalState.redirectTo);
          }
        }}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
      />
    </div>
  );
};

export default ConsteladorPage;
